import AddChannelButton from "components/shared/AddChannelButton";
import type { IChannel } from "containers/Channels/types";
import type { ChannelParams, ITermFormData } from "containers/TermFormContainer";
import { useTranslation } from "react-i18next";
import type { CalculationBaseColumn, ICalculationBaseColumnLabel } from "store/calculationBaseColumnLabelsStore";

import ChannelPercentages from "../ChannelPercentages";
import InactiveItemsCollapse from "../InactiveItemsCollapse";

interface ChannelsListSectionProps {
    mode: "new" | "edit";
    activeChannels: IChannel[];
    inactiveChannels: IChannel[];
    activedChannels?: { channelId: number }[];
    chosenChannels: ChannelParams[];
    activatedChannels: number[];
    usesTemplate?: boolean;
    handleChannelActivated: (
        e: React.ChangeEvent<HTMLInputElement>,
        channelId: number,
        calculationMethod: number,
    ) => void;
    handleChannelUpdates: (newChannel: unknown, groupId?: number) => void;
    handleToggleFormatTypeOverride: (channelId: number, formatTypeId: number, enabled: boolean) => void;
    handleCalculationBaseColumnChange: (
        channelId: number,
        calculationBaseColumn: CalculationBaseColumn,
        formatTypeId?: number | null,
    ) => void;
    handleMilestoneColumnChange: (
        channelId: number,
        milestoneBaseColumn: CalculationBaseColumn,
        formatTypeId?: number | null,
    ) => void;
    calculationBaseColumnLabels: ICalculationBaseColumnLabel[];
    useFormData: Pick<ITermFormData, "setError" | "clearErrors">;
    canCreateChannel?: boolean;
    onAddChannel?: () => void;
    /** Typology modal: place “new channel” at the top right, above the channel list. */
    addChannelAboveList?: boolean;
    formatTypesOnWork?: { id: number; name: string }[];
    from?: "templates" | "form";
    onShowDefaultConfigurationChange?: (channelId: number, showDefaultConfiguration: boolean) => void;
}

const ChannelsListSection = ({
    mode,
    activeChannels,
    inactiveChannels,
    activedChannels,
    chosenChannels,
    activatedChannels,
    usesTemplate,
    handleChannelActivated,
    handleChannelUpdates,
    handleToggleFormatTypeOverride,
    handleCalculationBaseColumnChange,
    handleMilestoneColumnChange,
    calculationBaseColumnLabels,
    useFormData,
    canCreateChannel,
    onAddChannel,
    addChannelAboveList = false,
    formatTypesOnWork = [],
    from = "form",
    onShowDefaultConfigurationChange,
}: ChannelsListSectionProps) => {
    const [t] = useTranslation();
    const { setError, clearErrors } = useFormData;

    const renderChannel = (channel: IChannel) => {
        const activeOnTitle = activedChannels?.some((item) => item.channelId === channel.id) ?? false;
        const currentlyActive =
            mode === "edit"
                ? !!chosenChannels?.some((item) => item.channelId === channel.id)
                : activatedChannels?.some((item) => item === channel.id);
        const existingChannel = chosenChannels?.find(
            (item) => item.channelId === channel.id && item.formatTypeId == null,
        );

        return (
            <ChannelPercentages
                channel={channel}
                currentlyActive={currentlyActive}
                activeOnTitle={activeOnTitle}
                existingChannel={existingChannel}
                mode={mode}
                handleChannelActivated={handleChannelActivated}
                handleChannelUpdates={handleChannelUpdates}
                handleToggleFormatTypeOverride={handleToggleFormatTypeOverride}
                chosenChannels={chosenChannels}
                handleCalculationBaseColumnChange={handleCalculationBaseColumnChange}
                handleMilestoneColumnChange={handleMilestoneColumnChange}
                calculationBaseColumnLabels={calculationBaseColumnLabels}
                formatTypesOnWork={formatTypesOnWork}
                key={`channel-milestones-${channel.id}`}
                usesTemplate={usesTemplate}
                useFormData={{ setError, clearErrors }}
                from={from}
                onShowDefaultConfigurationChange={onShowDefaultConfigurationChange}
            />
        );
    };

    const addChannelButton =
        canCreateChannel && onAddChannel ? (
            <AddChannelButton
                label={t("ConnectTitle.NewChannel")}
                onClick={onAddChannel}
                align="end"
                className={addChannelAboveList ? "channels-step-add-channel" : undefined}
            />
        ) : undefined;
    const showInactiveChannels = mode === "edit" && inactiveChannels.length > 0;

    return (
        <>
            {addChannelAboveList && addChannelButton}
            {activeChannels.map((channel) => renderChannel(channel))}
            {showInactiveChannels && (
                <InactiveItemsCollapse
                    label={t("ConnectTitle.InactiveChannelsCollapse", { count: inactiveChannels.length })}
                    trailingAction={addChannelAboveList ? undefined : addChannelButton}
                >
                    {inactiveChannels.map((channel) => renderChannel(channel))}
                </InactiveItemsCollapse>
            )}
        </>
    );
};

export default ChannelsListSection;
